<template>
    <div v-loading.body.lock="loading">
        <v-ajax-filters
                v-if="serverSide"
                :textCols="searchableColumns"
                :dateCols="searchableDateColumns"
                :numericCols="searchableNumericColumns"
                :loading="loading"
                @onSearch="query = $event"></v-ajax-filters>

        <table class="table table-bordered table-striped" :id="id"></table>
    </div>
</template>

<script>
  import _ from 'lodash'

  import VAjaxFilters from './AjaxFilters'
  import VSearchFilter from './SearchFilter'
  import VDateFilter from './DateFilter'

  export default {
    props: {
      tableData: Array,
      /*
      * Example:
      * headers: [
      *    {title: 'column 1', prop: 'col-1-data'},
      *    {title: 'column 2', prop: 'col-2-data'},
      *    {title: 'column 3', prop: 'col-3-data', class: 'some-class'}
      *  ]
      */
      headers: {
        type: Array,
        required: true
      },
      buttons: {
        type: Array,
        default () {
          return [
            {
              extend: 'copy',
              className: 'dt-button btn default'
            },
            {
              extend: 'csv',
              className: 'dt-button btn default'
            },
            {
              extend: 'pdf',
              className: 'dt-button btn default'
            },
            {
              extend: 'print',
              className: 'dt-button btn default'
            },
            {
              extend: 'colvis',
              className: 'dt-button btn default'
            }
          ]
        }
      },
      // Searchable text columns for AJAX requests
      // Array of objects with following structure: {title: columnTitle, prop: propertyPath}
      searchableColumns: {
        type: Array
      },
      // Searchable date columns for AJAX requests
      // Array of objects with following structure: {title: columnTitle, prop: propertyPath}
      searchableDateColumns: {
        type: Array
      },
      // Searchable numeric columns for AJAX requests
      // Array of objects with following structure: {title: columnTitle, prop: propertyPath}
      searchableNumericColumns: {
        type: Array
      },
      // additional params added to query string
      // ex.: {dealId:1234}
      preFilter: {
        type: Object,
        default () {
          return {}
        }
      },
      // datatable options
      // Show/hide searchbox
      search: {
        type: Boolean,
        default: true
      },
      // Show/hide pagination
      paging: {
        type: Boolean,
        default: true
      },
      fixedHeader: {
        type: Boolean,
        default: false
      },
      fixedColumns: {
        type: Boolean,
        default: false
      },
      // default records per page
      pageLength: {
        type: Number,
        default: 10
      },
      serverSide: {
        type: Boolean,
        dafault: false
      },
      // API endpoint for AJAX requests
      url: {
        type: String
      },
      dataFilter: {
        type: Function
      }
    },
    components: {
      VSearchFilter,
      VDateFilter,
      VAjaxFilters
    },
    data () {
      return {
        id: null,
        rows: [],
        dtHandle: null,
        searchableColumn: null,
        searchVal: null,
        loading: false,
        lastRequestData: {},
        totalItems: 0,
        query: {}
      }
    },
    computed: {
      page () {
        return this.dtHandle ? this.dtHandle.page() : null
      }
      // enabledSearchableColumns () {
      //   const columns = this.searchableColumns.map(c => {
      //     const idx = _.findIndex(this.headers, function (h) { return h.prop === c.prop })
      //     return Object.assign(this.headers[idx], {idx})
      //   })
      //
      //   return _.orderBy(columns, ['title'], ['asc'])
      // }
    },
    methods: {
      getCallabcks () {
        const vm = this
        return {
          createdRow (...args) {
            vm.$emit('created-row', ...args)
          },
          drawCallback (...args) {
            vm.$emit('draw', ...args)
          },
          footerCallback (...args) {
            vm.$emit('footer', ...args)
          },
          formatNumber (...args) {
            vm.$emit('format', ...args)
          },
          headerCallback (...args) {
            vm.$emit('header', ...args)
          },
          infoCallback (...args) {
            vm.$emit('info', ...args)
          },
          initComplete (...args) {
            vm.$emit('init', ...args)
          },
          preDrawCallback (...args) {
            vm.$emit('pre-draw', ...args)
          },
          rowCallback (...args) {
            vm.$emit('draw-row', ...args)
          },
          stateLoadCallback (...args) {
            vm.$emit('state-load', ...args)
          },
          stateLoaded (...args) {
            vm.$emit('state-loaded', ...args)
          },
          stateLoadParams (...args) {
            vm.$emit('state-load-params', ...args)
          },
          stateSaveCallback (...args) {
            vm.$emit('state-save', ...args)
          },
          stateSaveParams (...args) {
            vm.$emit('state-save-params', ...args)
          }
        }
      },
      getAjaxParams () {
        const vm = this
        return {
          url: this.url + '.jsonld',
          headers: {
            'Authorization': `Bearer ${this.$store.state.auth.idToken}`,
            'Accept': 'application/ld+json'
          },
          data (d) {
            const data = {
              itemsPerPage: d.length,
              draw: d.draw,
              page: vm.getCurrentPage() + 1 || 1,
              order: vm.mapOrder(d.order),
              ...vm.query,
              // ...vm.mapSearch(d.columns),
              ...vm.preFilter
            }
            // we can use this data later
            // in other API calls, ex. to get csv for current dataset
            vm.lastRequestData = data
            return data
          },
          beforeSend () {
            vm.loading = true
          },
          complete () {
            vm.loading = false
          },
          dataFilter: function (d) {
            if (!vm.dataFilter) {
              return d
            }

            const res = JSON.parse(d)
            vm.totalItems = res['recordsTotal']
            res['data'] = vm.dataFilter.dtFetch(res['data'])
            return JSON.stringify(res) // return JSON string
          }
        }
      },
      mapOrder (dtOrder) {
        const res = {}
        dtOrder.forEach(item => {
          res[this.getColumnByIndex(item.column)] = item.dir
        })
        return res
      },
      getColumnByIndex (index) {
        return this.headers[index].prop
      },
      updateRows (data) {
        const vm = this
        vm.rows = []
        data.forEach((item) => {
          let row = []

          vm.headers.forEach((header) => {
            row.push(item[header.prop])
          })

          vm.rows.push(row)
        })
      },
      getCurrentPage () {
        return this.page
      },
      initTable () {
        // Determine the height of the table based on where it is on the page,
        // or force a minimum of 300px if it's too close to the bottom of the window.
        let scrollY = (Math.floor(
          window.innerHeight - this.$el.getBoundingClientRect().top
        ) - 140)
        if (scrollY < 300) {
          scrollY = 300
        }
        if (this.tableData) {
          this.updateRows(this.tableData)
        }
        // Fire up datatables with our desired config
        // and store a reference handle to our component's
        // data element so we can reference it later..
        this.dtHandle = $(this.$el).find('table').DataTable({
          dom: this.serverSide
            ? `<'table-responsive't><'row'<'col-md-5 col-sm-5'l><'col-md-7 col-sm-7'p>>`
            : `<'row'<'col-md-6 col-sm-6'l><'col-md-6 col-sm-6'f>><'table-responsive't><'row'<'col-md-5 col-sm-5'i><'col-md-7 col-sm-7'p>>`,
          pageLength: this.pageLength,
          columns: this.transHeaders(this.headers),
          data: this.rows,
          searching: this.searching,
          paging: this.paging,
          fixedHeader: this.fixedHeader,
          fixedColumns: {
            leftColumns: 1
          },
          // scrollY: scrollY + 'px',
          scrollX: true,
          info: true,
          autoWidth: false,
          lengthMenu: this.ajax
            ? [
              [5, 10, 15, 20, -1],
              ['5', '10', '15', '20', 'All']
            ]
            : [
              [5, 10, 15, 50],
              ['5', '10', '15', '50']
            ],
          serverSide: this.serverSide,
          ajax: this.url ? this.getAjaxParams() : null,
          ...this.getCallabcks()
        })

        // Prepend the buttons to the wrapper so they appear above the table:
        // eslint-disable-next-line no-new
        new $.fn.dataTable.Buttons(this.dtHandle, {
          buttons: this.buttons
        })
        const $btnContainer = $(`#${this.id}`).closest('.portlet').find('.portlet-title .actions')
        this.dtHandle.buttons().container().appendTo($btnContainer.eq(0).get())
      },
      // translate table headers
      transHeaders (arr) {
        return arr.map(item => {
          const title = this.$te(`messages.${item.title}`) ? this.$t(`messages.${item.title}`) : item.title
          return Object.assign({}, item, {title})
        })
      }
    },
    watch: {
      tableData (val, oldVal) {
        this.updateRows(val)
        this.dtHandle.clear()
        this.dtHandle.rows.add(this.rows)
        this.dtHandle.draw()
      },
      query (val) {
        this.dtHandle.clear()
        this.dtHandle.draw()
      }
    },
    created () {
      this.id = _.uniqueId('datatable-')
    },
    mounted () {
      this.initTable()

      this.$on('draw', () => {
        // @todo Find a better way to do it
        $('.DTFC_LeftBodyLiner').css('top', '-11px')
      })
    },
    beforeDestroy () {
      this.dtHandle.destroy()
    }
  }
</script>



// WEBPACK FOOTER //
// src/app/components/datatables/Datatable.vue